/**
 * Lift2Fit — Health & Medical Disclaimer (v1).
 *
 * The document the signup checkbox names third. Short, direct, and free of any
 * claim that Lift2Fit can diagnose, treat or prescribe.
 */
import { LegalCallout, LegalList, LegalP, LegalSection } from './LegalLayout';
import { CONTACT_SUPPORT, DOMAIN, POLICY_VERSION, effectiveDateLabel } from './policy';

export function HealthDisclaimer() {
  return (
    <>
      <LegalCallout tone="warning" title="In an emergency, call your local emergency number">
        <LegalP>
          If you are experiencing chest pain, difficulty breathing, loss of consciousness, severe pain, or any
          other emergency symptom, stop exercising and contact emergency services immediately. Do not use Lift2Fit
          for emergency help, and do not delay seeking it because of anything the app says.
        </LegalP>
      </LegalCallout>

      <LegalSection id="not-provider" title="1. Lift2Fit is not a healthcare provider">
        <LegalP>
          Lift2Fit is a fitness tracking and coaching tool. We are <strong>not</strong> a doctor, physician,
          physiotherapist, physical therapist, dietician, nutritionist, psychologist, therapist or any other
          licensed healthcare professional, and we do not hold ourselves out as one.
        </LegalP>
        <LegalP>
          Nothing in Lift2Fit — the app, the website, the automated coach, a programme, a review, a message or a
          report — is medical advice, diagnosis, treatment, prescription or a clinical opinion. It is general
          fitness information and record-keeping only.
        </LegalP>
      </LegalSection>

      <LegalSection id="no-diagnosis" title="2. No diagnosis, treatment or prescription">
        <LegalP>
          Lift2Fit does not diagnose any condition, does not prescribe any treatment, exercise, medication or
          diet, and does not create a professional relationship of any kind between you and Lift2Fit. A suggestion
          to train a certain way is not a clinical recommendation that it is safe or appropriate for you.
        </LegalP>
        <LegalP>
          Do not use Lift2Fit to replace advice from a qualified professional, and do not disregard or delay
          professional advice because of something you read or were shown in the app.
        </LegalP>
      </LegalSection>

      <LegalSection id="consult" title="3. Consult a qualified professional first">
        <LegalP>
          Speak to a qualified healthcare professional before you start, restart or change a training programme,
          particularly if you:
        </LegalP>
        <LegalList
          items={[
            'have a heart condition, high blood pressure, diabetes, asthma or any other medical condition;',
            'are pregnant, have recently given birth, or are postpartum;',
            'are recovering from surgery, an injury or an illness;',
            'take medication that affects your heart rate, blood pressure, balance or blood sugar;',
            'have a history of an eating disorder, or are underweight;',
            'are returning to training after a long break, or are new to resistance training.',
          ]}
        />
        <LegalP>
          This list is not exhaustive. If you are unsure whether training is safe for you, ask a professional
          before you start.
        </LegalP>
      </LegalSection>

      <LegalSection id="risk" title="4. Assumption of risk and your responsibility">
        <LegalP>
          Physical training carries inherent risk, including muscle strain, joint injury, falls, cardiovascular
          events and, in rare cases, death. By using Lift2Fit you accept those risks and you accept that{' '}
          <strong>you are responsible for your own safety</strong>.
        </LegalP>
        <LegalP>
          You choose what to do, how much load to use, when to stop, and whether a movement is suitable for you.
          You are responsible for training within your ability, using safe equipment and technique, warming up
          appropriately, and following the safety instructions of your gym or equipment.
        </LegalP>
      </LegalSection>

      <LegalSection id="stop" title="5. Stop and seek help if something feels wrong">
        <LegalP>
          Stop exercising immediately and seek medical help if you experience any of the following:
        </LegalP>
        <LegalList
          items={[
            'chest pain, pressure or tightness, or pain spreading to your arm, neck or jaw;',
            'dizziness, faintness, light-headedness or confusion;',
            'shortness of breath that does not settle, or an irregular or racing heartbeat;',
            'sharp or worsening joint or muscle pain, numbness or tingling;',
            'nausea, vomiting, or a sudden severe headache;',
            'any other symptom that worries you.',
          ]}
        />
        <LegalP>
          Persistent pain is a signal, not a target to push through. Seek a qualified professional rather than
          training around it.
        </LegalP>
      </LegalSection>

      <LegalSection id="automated" title="6. Automated coaching can be wrong">
        <LegalP>
          Lift2Fit’s coaching suggestions are generated automatically from the data in your log. They can be
          incomplete, out of date, or unsuitable for your circumstances, and no human reviews every suggestion
          before you see it. Automated output is <strong>not personalised medical advice</strong>. You decide
          whether to follow it, and you can edit or ignore anything it produces.
        </LegalP>
        <LegalP>
          If a suggestion conflicts with advice from your doctor or physiotherapist, follow the professional.
        </LegalP>
      </LegalSection>

      <LegalSection id="pain-data" title="7. Pain, injury and wellness data">
        <LegalP>
          If you record pain scores, injury flags or wellness check-ins, that information is used to adjust your
          training suggestions and, where you have consented, to improve our models. It is not a clinical
          assessment and it is not monitored by a medical professional. If you record something that concerns
          you, do not wait for the app to act — seek help yourself.
        </LegalP>
      </LegalSection>

      <LegalSection id="coach-programmes" title="8. Coach-written programmes">
        <LegalP>
          If you train with a coach through Lift2Fit, the programme is the coach’s, not ours. Coaches on the
          platform are independent, we do not employ or supervise them, and we do not review or validate the
          safety of what they prescribe. If you have a concern about a programme, raise it with your coach; if you
          believe it is unsafe, stop and seek professional advice.
        </LegalP>
      </LegalSection>

      <LegalSection id="contact" title="9. Contact">
        <LegalP>
          Questions about this disclaimer: {CONTACT_SUPPORT}. This document is version {POLICY_VERSION} and takes
          effect on {effectiveDateLabel()}.
        </LegalP>
        <LegalP>
          Lift2Fit · {DOMAIN}. One stop for all your fitness needs.
        </LegalP>
      </LegalSection>
    </>
  );
}
